import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import { Button, Card, CardContent, CardHeader } from "@mui/material";
import CompletionSVGIcon from "../Assets/Images/completion.svg";
import StatusChangeSVGIcon from "../Assets/Images/status_change.svg";
import AttentionSVGIcon from "../Assets/Images/attention.svg";
import dayjs from "dayjs";
import { ColorEnum } from "../Assets/Colors/ColorsEnum";
import RightIcon from "../Assets/Images/right.svg";
import getTenantIdFromURL from "../utils/getTenantIdFromURL";
import { useSelector } from "react-redux";
import {getCompanyByName} from "../api/companyApi";
import alertService from "../api/tenantApi";
import {getTenantInfo} from "../utils/getTenantInfo";
import { useState, useEffect } from "react";
import dashboardPreferencesService from "../api/tenantApi";
import { getCurrentUserId } from "../authUtils";
import { loadInAppNotificationPreferences, isNotificationVisible } from "../utils/notificationVisibility";
import { DLOCAL_NOTIFICATION_TENANT_ID } from "../utils/notificationPreferenceSettings";
import { updateAlertById } from "../api/tenantApi";
import { subscribe, unsubscribe } from "../events";
import { companyReportPath } from "../utils/tenantRouting";

const drawerWidth = 300;
const NOTIFICATIONS_PER_PAGE = 20;

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  position: "relative",
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: "flex-start",
}));

export default function NotificationDrawer(props) {
  const [tenant, setTenant] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const URLTenant = getTenantIdFromURL() || tenant?.tenantId;
  const isAdmin = useSelector(state => state.userInfo.isAdmin);
  const isDistributor = useSelector(state => state.userInfo.isDistributor);

  useEffect(() => {
    const handleRefetch = () => setRefetchTrigger(prev => prev + 1);
    subscribe('refetch_notifications', handleRefetch);

    return () => {
      unsubscribe('refetch_notifications', handleRefetch);
    };
  }, []);

  // Poll periodically so notifications that arrive without a WebSocket event — most
  // importantly the async risk alerts (Critical Risk / MAJOR RISK) produced by monthly
  // refreshes and background rescoring — show up in the bell without a manual reload.
  // Bumping refetchTrigger re-runs the badge-count effect below (and refreshes page 1
  // of the list when the drawer is open). Pause polling when the tab is hidden.
  useEffect(() => {
    const POLL_MS = 45000;
    const tick = () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      setRefetchTrigger(prev => prev + 1);
    };
    const iv = setInterval(tick, POLL_MS);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    const fetchTenantInfo = async () => {
      try {
        const tenantInfo = await getTenantInfo();
        setTenant(tenantInfo);
      } catch (error) {
        console.error('Error fetching tenant info in NotificationDrawer:', error);
      }
    };

    fetchTenantInfo();
  }, []);

  const fetchMoreNotificationsOnLoadMore = async (pageNum = 0) => {
    if (loading) return;
    console.log("🔍 [NotificationDrawer] fetchNotifications triggered", {pageNum, loading});
    
    setLoading(true);
    try {
      const tenantId = URLTenant || tenant?.tenantId;
      if (!tenantId) {
        setLoading(false);
        return;
      }

      // Fetch user's notification preferences (user-specific for in-app)
      const userId = getCurrentUserId();
      const preferences = await loadInAppNotificationPreferences(dashboardPreferencesService, userId, tenantId);

      // Fetch notifications with pagination (page starts from 1 in the API)
      const response = await alertService.fetchPaginatedAlertsByTenantId(
        tenantId, 
        pageNum + 1, // API expects 1-based page numbers
        NOTIFICATIONS_PER_PAGE
      );

      const allNotifications = response?.alerts || [];
      
      // Filter notifications based on user preferences
      const filteredNotifications = allNotifications.filter(alert => isNotificationVisible(alert, preferences, userId, tenantId));

      // Use API's hasNextPage for pagination instead of calculating from total
      const hasNextPage = response?.hasNextPage || false;
      
      const updated = [...notifications, ...filteredNotifications];
      setNotifications(updated);
      setHasMore(hasNextPage);
      if (tenantId === DLOCAL_NOTIFICATION_TENANT_ID) {
        setTotalCount(updated.length);
        props.onNotificationCountChange?.(updated.length);
      } else {
        setTotalCount(response?.total || 0);
      }
      
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch when drawer opens or tenant changes
  useEffect(() => {
    if (props.open && (URLTenant || tenant?.tenantId)) {
      setPage(0);
      setNotifications([]);
      setHasMore(true);
    }
  }, [URLTenant, tenant?.tenantId]);

  // Fetch notification count for badge when component mounts
  useEffect(() => {
    const fetchNotificationCount = async () => {
      try {
        const tenantId = URLTenant || tenant?.tenantId;
        console.log("🔍 [NotificationDrawer] Badge count effect triggered", {URLTenant, tenantId: tenant?.tenantId, onNotificationCountChange: !!props.onNotificationCountChange});
        if (!tenantId) return;

        // Fetch user's notification preferences (user-specific for in-app)
        const userId = getCurrentUserId();
        const preferences = await loadInAppNotificationPreferences(dashboardPreferencesService, userId, tenantId);

        // Fetch first page with full page size to get total count
        const response = await alertService.fetchPaginatedAlertsByTenantId(tenantId, 1, NOTIFICATIONS_PER_PAGE);
        
        const allNotifications = response?.alerts || [];

        // Filter notifications based on user preferences
        const filteredNotifications = allNotifications.filter(alert => isNotificationVisible(alert, preferences, userId, tenantId));

      // Use API's hasNextPage for pagination instead of calculating from total
      const hasNextPage = response?.hasNextPage || false;
        
      const filteredCount = filteredNotifications.length;
        // Filter notifications based on user preferences for accurate count
      setTotalCount(filteredCount);

      setNotifications(filteredNotifications);
      setHasMore(hasNextPage);
        
      // Notify parent about notification count for badge
      if (props.onNotificationCountChange) {
          props.onNotificationCountChange(filteredCount);
      }
      } catch (error) {
        console.error('Error fetching notification count:', error);
      }
    };

    if (URLTenant || tenant?.tenantId) {
      fetchNotificationCount();
    }
  }, [URLTenant, tenant?.tenantId, refetchTrigger]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchMoreNotificationsOnLoadMore(nextPage);
  };

  const handleDrawerClose = () => {
    props.close();
  };

  const handleAlertClick = async (params) => {
    try {
      let companyInfo;

      if (typeof params.companyInfo === 'string') {
        companyInfo = JSON.parse(params.companyInfo);
      } else if (typeof params.companyInfo === 'object') {
        companyInfo = params.companyInfo;
      } else {
        // For Status Change notifications that might not have companyInfo,
        // try to extract company name from the message
        if (params.type === "Status Change" && params.message) {
          const companyNameMatch = params.message.match(/for (.+?) on/);
          if (companyNameMatch && companyNameMatch[1]) {
            companyInfo = {
              companyName: companyNameMatch[1]
            };
          }
        }
        
        if (!companyInfo) {
          companyInfo = {
            companyName: params.companyName,
          };
        }
      }

      const companyName = companyInfo?.companyName;
      const tenantId = params.tenantId || URLTenant;

      if (!companyName || !tenantId) {
        console.warn('Missing companyName or tenantId for notification click:', { 
          companyName, 
          tenantId, 
          notificationType: params.type,
          hasCompanyInfo: !!params.companyInfo 
        });
        return;
      }

      const result = await getCompanyByName(tenantId, companyName);
      const company = result?.data;

      if (!company?.id) {
        console.warn("Company not found for:", companyName);
        return;
      }

      if (params.type === "Status Change" && params?.approvers && params?.approvers?.length > 0 && params?.approvers?.includes(getCurrentUserId())) {
        try {
          const updatedAlert = await updateAlertById(params.id, {
            ...params,
            approvers: params?.approvers.filter(approver => approver !== getCurrentUserId())
          });
          console.log("🔍 [NotificationDrawer] Updated alert:", updatedAlert);
          setNotifications(prev => {
            const filtered = prev.filter(notification => notification.id !== params.id);
            return filtered;
          });
        } catch (error) {
          console.error("❌ [NotificationDrawer] Error updating alert:", error);
        }
      }

      const companyUrl = companyReportPath({
        isAdmin,
        isDistributor,
        tenantId,
        companyId: company.id,
      });

      window.open(companyUrl, "_blank");

    } catch (error) {
      console.error('Error handling alert click:', error);
    }
  };

  
  const handleHideAlert = async (alert) => {
    try {
      await alertService.deleteAlertById(alert.id);
      
      // Remove from local state and update counts
      setNotifications(prev => {
        const filtered = prev.filter(notification => notification.id !== alert.id);
        return filtered;
      });
      
      const newTotalCount = totalCount - 1;
      setTotalCount(newTotalCount);
      
      // Notify parent about count change for badge
      if (props.onNotificationCountChange) {
        props.onNotificationCountChange(newTotalCount);
      }
    } catch (error) {
      console.error("❌ Error deleting alert:", error);
    }
  };

  const notificationTypes = {
    "Status Change": (alert) =>(
      <CardHeader
        avatar={
          <Box
            sx={{
              width: "28px",
              height: "28px",
              background: "#3753A033",
              borderRadius: "50%",
              opacity: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img width="16px" src={StatusChangeSVGIcon} alt="Icon" />
          </Box>
        }
        title="Status Change"
        titleTypographyProps={{
          color: ColorEnum.DARK_BLUE_SHADE2,
          opacity: 1,
          fontWeight: "bold",
        }}
        action={
          <Button
            variant="text"
            sx={{
              color: ColorEnum.PRIMARY_BLUE,
              fontSize: 10,
              textDecoration: "underline",
              textTransform: "capitalize",
              top: 3,
            }}
             onClick={(e) => {
              e.stopPropagation();
              handleHideAlert(alert);
            }}
          >
            Hide
          </Button>
        }
      />
    ),
    Complete: (alert) => (
      <CardHeader
        avatar={
          <Box
            sx={{
              width: "28px",
              height: "28px",
              background: "#60AC5033",
              borderRadius: "50%",
              opacity: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img width="16px" src={CompletionSVGIcon} alt="Icon" />
          </Box>
        }
        title="Process Completed"
        titleTypographyProps={{
          color: ColorEnum.GREEN,
          opacity: 1,
          fontWeight: "bold",
        }}
        action={
          <Button
            variant="text"
            sx={{
              color: ColorEnum.PRIMARY_BLUE,
              fontSize: 10,
              textDecoration: "underline",
              textTransform: "capitalize",
              top: 3,
            }}
             onClick={(e) => {
              e.stopPropagation();
              handleHideAlert(alert);
            }}
          >
            Hide
          </Button>
        }
      />
    ),
    Update: (alert) => (          
      <CardHeader
        avatar={
          <Box
            sx={{
              width: "28px",
              height: "28px",
              background: "#3753A033",
              borderRadius: "50%",
              opacity: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img width="16px" src={AttentionSVGIcon} alt="Icon" />
          </Box>
        }
        title="Attention"
        titleTypographyProps={{
          color: ColorEnum.DARK_BLUE_SHADE2,
          opacity: 1,
          fontWeight: "bold",
        }}
        action={
          <Button
            variant="text"
            sx={{
              color: ColorEnum.PRIMARY_BLUE,
              fontSize: 10,
              textDecoration: "underline",
              textTransform: "capitalize",
              top: 3,
            }}
             onClick={(e) => {
              e.stopPropagation();
              handleHideAlert(alert);
            }}
          >
            Hide
          </Button>
        }
      />
    ),
    "Critical Risk Alert": (alert) => (
      <CardHeader
        avatar={
          <Box
            sx={{
              width: "28px",
              height: "28px",
              background: "#FF444433",
              borderRadius: "50%",
              opacity: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img width="16px" src={AttentionSVGIcon} alt="Icon" />
          </Box>
        }
        title="Critical Risk Alert"
        titleTypographyProps={{
          color: "#FF4444",
          opacity: 1,
          fontWeight: "bold",
        }}
        action={
          <Button
            variant="text"
            sx={{
              color: ColorEnum.PRIMARY_BLUE,
              fontSize: 10,
              textDecoration: "underline",
              textTransform: "capitalize",
              top: 3,
            }}
             onClick={(e) => {
              e.stopPropagation();
              handleHideAlert(alert);
            }}
          >
            Hide
          </Button>
        }
      />
    ),
  };

  notificationTypes['Financial Report'] = alert => React.cloneElement(notificationTypes.Complete(alert), { title: 'Financial Report' });
  notificationTypes.Error = alert => React.cloneElement(notificationTypes.Update(alert), { title: 'Processing Error' });

  return (
    <Drawer
      variant="permanent"
      anchor="right"
      open={props.open}
      sx={{
        width: props.open ? drawerWidth : 0,
        flexShrink: 0,
        opacity: props.open ? 1 : 0,
        height: props.open ? "100%" : "0",
        whiteSpace: "nowrap",
        transition: "width 1.0s, height 1.5s, opacity 1.5s",
        zIndex: props.open ? "5" : "-1",

        '& .MuiDrawer-paper': {
          '::-webkit-scrollbar': {
            width: 0,
            height: 0,
          },
        },
      }}
    >
      <DrawerHeader >
        <div>
          <IconButton
            onClick={handleDrawerClose}
            style={{ position: "relative", left: "-10px", zIndex: 10 }}
          >
            {props.open ? (
              <img src={RightIcon} alt="Expand Less" width="33" height="33" />
            ) : null}
          </IconButton>
        </div>
        <Typography variant="h6" style={{ color: "#2F3D63", fontWeight: "600" }}>
          Notifications
        </Typography>
      </DrawerHeader>
      <Divider />
      {notifications.length > 0 ? (
        <>
          <List>
            {notifications.map((item, index) => (
              <Card
                key={index}
                sx={{ maxWidth: 280, margin: "10px", backgroundColor: "#1A2A5614" }}
              >
                {notificationTypes[item.type]
                  ? notificationTypes[item.type](item)
                  : notificationTypes.Complete(item)}
                <CardContent
                  sx={{ position: "relative", bottom: 12, cursor: "pointer" }}
                  onClick={() => handleAlertClick(item)}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }} color={ColorEnum.TEXT_DARK_BLUE}>
                    {item.message}
                  </Typography>
                  <Typography
                    color={ColorEnum.GREY}
                    fontSize={12}
                    position={"relative"}
                    top={15}
                  >
                    {dayjs(item.createdAt).format("YYYY-MM-DD HH:mm:ss")}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </List>
          
          {/* Load More Button */}
          {hasMore && (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <Button
                variant="outlined"
                onClick={handleLoadMore}
                disabled={loading}
                sx={{
                  width: "90%",
                  color: ColorEnum.PRIMARY_BLUE,
                  borderColor: ColorEnum.PRIMARY_BLUE,
                  textTransform: "none",
                  fontSize: "12px",
                  py: 1,
                  "&:hover": {
                    backgroundColor: ColorEnum.PRIMARY_BLUE,
                    color: "white",
                  },
                  "&:disabled": {
                    opacity: 0.6,
                  },
                }}
              >
                {loading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="caption">Loading</Typography>
                    <Box sx={{ 
                      width: 12, 
                      height: 12, 
                      border: "2px solid #ccc", 
                      borderTop: "2px solid #1976d2",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                      "@keyframes spin": {
                        "0%": { transform: "rotate(0deg)" },
                        "100%": { transform: "rotate(360deg)" }
                      }
                    }} />
                  </Box>
                ) : (
                  "Load More"
                )}
              </Button>
            </Box>
          )}
          
          <Divider />
        </>
      ) : (
        <Box sx={{ p: 3, textAlign: "center", color: "#888" }}>
          <Typography variant="body2">
            {loading ? "Loading notifications..." : "No notifications"}
          </Typography>
        </Box>
      )}
    </Drawer>
  );
}
